// src/pages/finance-portal/reports/BudgetVsActual.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
    Autocomplete,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, formatPercent, downloadCsv,
} from './reportShared';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function BudgetVsActual() {
    const [budgets, setBudgets] = useState([]);
    const [selectedBudgetId, setSelectedBudgetId] = useState('');
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Create/edit dialog
    const [dialog, setDialog] = useState({ open: false, budget: null });
    const [accounts, setAccounts] = useState([]);
    const [form, setForm] = useState({
        name: '', fiscal_year: new Date().getFullYear(),
        start_date: `${new Date().getFullYear()}-01-01`,
        end_date: `${new Date().getFullYear()}-12-31`,
        description: '', lines: [],
    });

    const fetchBudgets = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/budgets');
            if (res.data.success) {
                setBudgets(res.data.data || []);
                if (res.data.data?.length && !selectedBudgetId) {
                    setSelectedBudgetId(res.data.data[0].id);
                }
            }
        } catch (e) { /* ignore */ }
    }, [selectedBudgetId]);

    useEffect(() => { fetchBudgets(); }, [fetchBudgets]);

    useEffect(() => {
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
                if (res.data.success) setAccounts(res.data.data || []);
            } catch (e) { /* silent */ }
        })();
    }, []);

    const runReport = useCallback(async () => {
        if (!selectedBudgetId) { setReport(null); return; }
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/budget-vs-actual', {
                params: { budget_id: selectedBudgetId },
            });
            if (res.data.success) setReport(res.data);
            else setError(res.data.message || 'Failed to load');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally { setLoading(false); }
    }, [selectedBudgetId]);

    useEffect(() => { runReport(); }, [runReport]);

    const openCreate = () => {
        setForm({
            name: '', fiscal_year: new Date().getFullYear(),
            start_date: `${new Date().getFullYear()}-01-01`,
            end_date: `${new Date().getFullYear()}-12-31`,
            description: '', lines: [],
        });
        setDialog({ open: true, budget: null });
    };

    const openEdit = async (bId) => {
        try {
            const res = await axiosInstance.get(`/api/finance/budgets/${bId}`);
            if (res.data.success) {
                const b = res.data.data;
                setForm({
                    name: b.name, fiscal_year: b.fiscal_year,
                    start_date: b.start_date, end_date: b.end_date,
                    description: b.description || '', lines: b.lines || [],
                });
                setDialog({ open: true, budget: b });
            }
        } catch (e) { /* ignore */ }
    };

    const saveBudget = async () => {
        try {
            if (dialog.budget) {
                await axiosInstance.put(`/api/finance/budgets/${dialog.budget.id}`, form);
            } else {
                await axiosInstance.post('/api/finance/budgets', form);
            }
            setDialog({ open: false, budget: null });
            fetchBudgets();
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to save budget');
        }
    };

    const deleteBudget = async (bId) => {
        if (!window.confirm('Delete this budget?')) return;
        try {
            await axiosInstance.delete(`/api/finance/budgets/${bId}`);
            setSelectedBudgetId('');
            fetchBudgets();
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to delete');
        }
    };

    const addLine = () => {
        setForm((f) => ({
            ...f, lines: [...f.lines, { account_id: '', period_month: 1, amount: 0 }],
        }));
    };

    const updateLine = (idx, patch) => {
        setForm((f) => ({
            ...f,
            lines: f.lines.map((ln, i) => i === idx ? { ...ln, ...patch } : ln),
        }));
    };

    const removeLine = (idx) => {
        setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
    };

    const exportCsv = () => {
        if (!report) return;
        const rows = report.rows.map((r) => {
            const o = {
                account: `${r.account_number} ${r.account_name}`,
                type: r.account_type,
                ytd_budget: r.ytd_budget,
                ytd_actual: r.ytd_actual,
                ytd_variance: r.ytd_variance,
                ytd_variance_percent: r.ytd_variance_percent,
            };
            r.periods.forEach((p) => {
                o[`m${p.month}_budget`] = p.budget;
                o[`m${p.month}_actual`] = p.actual;
            });
            return o;
        });
        downloadCsv(`budget_vs_actual_${report.budget?.fiscal_year || ''}.csv`, rows);
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CompareArrowsIcon sx={{ color: ACCENT }} />
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Budget vs Actual</Typography>
                </Stack>
                <Button startIcon={<AddIcon />} variant="contained"
                    sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}
                    onClick={openCreate}>New Budget</Button>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Budget</InputLabel>
                            <Select label="Budget" value={selectedBudgetId}
                                onChange={(e) => setSelectedBudgetId(e.target.value)}>
                                {budgets.map((b) => (
                                    <MenuItem key={b.id} value={b.id}>
                                        {b.name} — FY{b.fiscal_year} ({b.status})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
                        {selectedBudgetId && (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <IconButton onClick={() => openEdit(selectedBudgetId)}><EditIcon /></IconButton>
                                <IconButton onClick={() => deleteBudget(selectedBudgetId)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                                <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>CSV</Button>
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {report && !loading && (
                <Paper sx={{ p: 2, overflowX: 'auto' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account</TableCell>
                                    {(report.months || []).map((m) => (
                                        <TableCell key={m} align="right">{MONTH_LABELS[m - 1]}</TableCell>
                                    ))}
                                    <TableCell align="right">YTD Budget</TableCell>
                                    <TableCell align="right">YTD Actual</TableCell>
                                    <TableCell align="right">Variance</TableCell>
                                    <TableCell align="right">Var %</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.map((r) => (
                                    <TableRow key={r.account_id}>
                                        <TableCell>{r.account_number} — {r.account_name}</TableCell>
                                        {r.periods.map((p) => (
                                            <TableCell key={p.month} align="right">
                                                {p.actual ? formatCurrency(p.actual) : '—'}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right">{formatCurrency(r.ytd_budget)}</TableCell>
                                        <TableCell align="right">{formatCurrency(r.ytd_actual)}</TableCell>
                                        <TableCell align="right"
                                            sx={{ color: r.favorable ? ACCENT : '#ef4444' }}>
                                            {formatCurrency(r.ytd_variance)}
                                        </TableCell>
                                        <TableCell align="right"
                                            sx={{ color: r.favorable ? ACCENT : '#ef4444' }}>
                                            {formatPercent(r.ytd_variance_percent)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #111827', fontWeight: 700 } }}>
                                    <TableCell colSpan={(report.months || []).length + 1}>Totals</TableCell>
                                    <TableCell align="right">{formatCurrency(report.totals?.total_budget)}</TableCell>
                                    <TableCell align="right">{formatCurrency(report.totals?.total_actual)}</TableCell>
                                    <TableCell align="right">{formatCurrency(report.totals?.total_variance)}</TableCell>
                                    <TableCell align="right">—</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, budget: null })}
                maxWidth="md" fullWidth>
                <DialogTitle>{dialog.budget ? 'Edit Budget' : 'New Budget'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Name" fullWidth size="small"
                                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField label="Fiscal Year" type="number" fullWidth size="small"
                                value={form.fiscal_year}
                                onChange={(e) => setForm({ ...form, fiscal_year: parseInt(e.target.value, 10) })} />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField label="Start" type="date" fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                                value={form.start_date}
                                onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField label="End" type="date" fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                                value={form.end_date}
                                onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Description" fullWidth size="small" multiline rows={2}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1">Budget Lines</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add Line</Button>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300, mt: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account</TableCell>
                                    <TableCell>Month</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {form.lines.map((ln, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <Autocomplete size="small" options={accounts}
                                                getOptionLabel={(a) => a ? `${a.account_number} — ${a.name}` : ''}
                                                value={accounts.find((a) => a.id === ln.account_id) || null}
                                                onChange={(_, v) => updateLine(i, { account_id: v?.id })}
                                                renderInput={(p) => <TextField {...p} size="small" />}
                                                sx={{ minWidth: 260 }} />
                                        </TableCell>
                                        <TableCell>
                                            <Select size="small" value={ln.period_month}
                                                onChange={(e) => updateLine(i, { period_month: parseInt(e.target.value, 10) })}>
                                                {MONTH_LABELS.map((m, idx) => (
                                                    <MenuItem key={idx + 1} value={idx + 1}>{m}</MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" type="number" value={ln.amount}
                                                onChange={(e) => updateLine(i, { amount: parseFloat(e.target.value) || 0 })} />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => removeLine(i)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ open: false, budget: null })}>Cancel</Button>
                    <Button variant="contained" onClick={saveBudget}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
